import { models, utils } from '@kurtosys/ksys-app-template';
import { AppStoreBase } from './AppStoreBase';
import { IAppComponents } from '../models/IAppComponents';
import { action, computed, observable } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { Manifest } from '../../../configuration/Manifest';
import { IGridProps } from '@kurtosys/ksys-app-components/dist/components/base/Grid/models';
import { IApplicationEmbedInput } from '@kurtosys/ksys-app-components/dist/components/base/ApplicationEmbed/models';
import { IInputs } from '../../../models/app/IInputs';
import { IEventBusEvent, IEventBusEventDetail } from '../../shared/IEventTargetWithDataset';

/* [Component: appStoreComponentImport] */

export class AppStore extends AppStoreBase {
	@observable.ref gridComponentTemplates: IEventBusEventDetail[] = [];
	@observable.ref gridComponentsHaveData: boolean = false;

	constructor(element: HTMLElement, url: string, storeContext: StoreContext, manifest: Manifest) {
		super(element, url, storeContext, manifest);
		this.initializeGridAppEvents();
	}

	initializeGridAppEvents = () => {
		this.setPreventAppLoadedEventFire(true);

		// Trigger app initialized is in a timeout to initialize the grid
		// when all apps have loaded or after 5 seconds
		const initializeGridApp = () => {
			this.setIsBootstrapped();
			this.triggerAppInitialized();
			if (this.storeContext.eventBusStore && this.storeContext.eventBusStore.helper) {
				this.storeContext.eventBusStore.helper.unregister(models.eventBus.EventType.appInitialized, appInitializedFn);
			}
		};
		const timeoutFn = setTimeout(initializeGridApp, 5000);

		const appInitializedFn = (event: unknown) => {
			const { detail } = event as IEventBusEvent;
			const currentTemplateIdx = this.gridComponentTemplates.findIndex(template => template.appGuid === detail.appGuid);

			this.updateGridTemplateAtIndex(currentTemplateIdx, { initialized: true, ...detail });
			this.updateGridComponentsHaveData(this.gridComponentTemplates.some(template => template.hasData));

			if (this.gridComponentTemplates.every(template => template.initialized)) {
				initializeGridApp();
				clearTimeout(timeoutFn);
			}
		};

		if (this.storeContext.eventBusStore && this.storeContext.eventBusStore.helper) {
			this.storeContext.eventBusStore.helper.register(models.eventBus.EventType.appInitialized, appInitializedFn);
		}
	}

	@action
	addGridTemplate = (template: IEventBusEventDetail) => {
		this.gridComponentTemplates = [...this.gridComponentTemplates, template];
	}

	@action
	updateGridTemplateAtIndex = (templateIdx: number, template: IEventBusEventDetail) => {
		this.gridComponentTemplates[templateIdx] = { ...template };
	}

	@action
	updateGridComponentsHaveData = (hasData: boolean) => {
		this.gridComponentsHaveData = this.gridComponentTemplates.some(template => template.hasData);
	}

	@computed
	get hasData(): boolean {
		return this.gridComponentsHaveData || !!this.gridProps;
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get rawGridProps(): IGridProps {
		return (this.appComponentConfiguration && this.appComponentConfiguration.gridProps) || {};
	}

	@computed
	get gridProps(): IGridProps | undefined {
		const gridProps: IGridProps = {
			...(utils.object.deepMergeObjects({}, this.rawGridProps)),
		};
		if (!utils.typeChecks.isNullOrEmpty(gridProps.breakpoints)) {
			gridProps.breakpoints.forEach((breakpoint) => {
				if (breakpoint.props) {
					this.orchestrateGridProps(breakpoint.props);
					breakpoint.props.queryStore = this.storeContext.queryStore;
					breakpoint.props.loadUiIds = true;
				}
			});
		}
		this.orchestrateGridProps(gridProps);
		gridProps.queryStore = this.storeContext.queryStore;
		gridProps.loadUiIds = true;
		return gridProps; //
	}

	orchestrateGridProps = (gridProps: IGridProps) => {
		const { grid } = gridProps;

		if (grid) {
			const inputs: Partial<IInputs> = this.appParamsHelper.inputs || {};
			const embedInputs: IApplicationEmbedInput[] = (Object.keys(inputs) as (keyof IInputs)[]).map((key) => {
				const value = inputs[key] as any;
				return {
					key,
					value,
				};
			});
			const { rows = [] } = grid;
			rows.forEach((row) => {
				if (row) {
					const { columns = [] } = row;
					columns.forEach((column) => {
						if (column) {
							const { components = [] } = column;
							components.forEach((component) => {
								if (component) {
									switch (component.key) {
										case 'base.applicationEmbed':
											component.props = this.orchestrateEmbed(component.props, 'ksys-app-other', embedInputs);
											this.addGridTemplate({ appGuid: component.props.applicationCode });
											break;
									}
								}
							});
						}
					});
				}
			});
		}
		return gridProps;
	}

	async customInitializeAfter() {

	}
}